import { Injectable } from '@angular/core';
import { Request } from '../models/tracer.model';

@Injectable({
  providedIn: 'root',
})
export class TraceUtilsService {
  /**
   * Build-version badge data, keyed by request id.
   *
   * v1 recorded an env/commit Version per request and rendered it as a badge.
   * v2 does not, so this stays permanently empty — kept rather than removed
   * because the templates read it in several places and an empty map renders
   * nothing, which is the behaviour we want.
   */
  versions: { [key: string]: { env?: string; hash?: string; message?: string } } = {};

  constructor() {}

  getEnv(request: Request): string {
    return request?.VersionID && this.versions[request.VersionID]?.env
      ? (this.versions[request.VersionID]?.env ?? '')
      : '(not set)';
  }

  getHash(request: Request): string {
    return request?.VersionID && this.versions[request.VersionID]?.hash
      ? (this.versions[request.VersionID]?.hash ?? '')
      : '(not set)';
  }

  getProviders(request: Request): string {
    // For multi-provider requests (typically AirShopping fanning out to
    // several NDC/Kyte providers) render "*" instead of expanding the
    // list. Two equivalent inputs need to map to "*":
    //
    //   1. Multiple requester traces with different Providers values
    //      (e.g. one trace per provider after fan-out).
    //   2. A single Providers value that already represents intent —
    //      either the literal "*" wildcard or a comma-separated list
    //      like "EK,CM,AV,BA,A3" (sidecar carries consumer intent
    //      verbatim, which can land on traces.providers OR fall back
    //      via request.provider).
    //
    // Collapse both into "*" so the dashboard reads consistently
    // regardless of which capture path landed first.
    const isMultiIntent = (v: string | undefined | null): boolean =>
      !!v && (v === '*' || v.includes(','));

    const traceProviders = (request?.Traces ?? [])
      .filter((t) => t.Source === 'requester' && !!t.Providers)
      .map((t) => t.Providers);

    if (traceProviders.some(isMultiIntent)) return '*';

    const distinct = new Set(traceProviders);
    if (distinct.size > 1) return '*';
    if (distinct.size === 1) return distinct.values().next().value ?? '(not set)';

    // Fallback to request.Provider — same multi-intent collapse.
    if (isMultiIntent(request?.Provider)) return '*';
    return request?.Provider || '(not set)';
  }
}
